import type { InputHTMLAttributes, KeyboardEvent } from "react";

/** Strip scanner suffixes and surrounding whitespace from a floor serial field. */
export function normalizeFloorSerialInput(raw: string): string {
  return raw.replace(/[\r\n]+$/g, "").trim();
}

export function floorSerialInputHasScannerTerminator(raw: string): boolean {
  return /[\r\n]/.test(raw);
}

/** Read the committed value from the DOM first (mobile IME), then React state. */
export function readFloorSerialFromInput(
  input: HTMLInputElement | null | undefined,
  stateValue: string,
): string {
  const fromDom = input?.value;
  return normalizeFloorSerialInput(fromDom ?? stateValue);
}

export const FLOOR_SCAN_INPUT_FIELD_PROPS = {
  autoComplete: "off",
  autoCapitalize: "off",
  autoCorrect: "off",
  enterKeyHint: "go",
  inputMode: "none",
  className: "min-h-12 font-mono text-base",
} as const satisfies Partial<InputHTMLAttributes<HTMLInputElement>>;

/** Props for modal or form fields where operators type MPNs, not scan-only surfaces. */
/** Hub typing fields use this attribute so the tablet launcher can show HeliBoard. */
export const SGS_TYPING_FIELD_ATTRIBUTE = "data-sgs-typing-field";

export const FLOOR_TYPING_INPUT_PROPS = {
  type: "text",
  inputMode: "text",
  autoComplete: "off",
  autoCapitalize: "characters",
  autoCorrect: "off",
  spellCheck: false,
  enterKeyHint: "go",
  [SGS_TYPING_FIELD_ATTRIBUTE]: "",
} as const;

/** Undo tablet launcher scan-surface IME suppression on a typing field. Idempotent ??? safe on every focus. */
export function ensureFloorTypingInputKeyboard(input: HTMLInputElement | null | undefined): void {
  if (input == null) return;
  if (input.getAttribute("inputmode") !== "text") {
    input.setAttribute("inputmode", "text");
  }
  if (input.hasAttribute("data-sgs-scan-target")) {
    input.removeAttribute("data-sgs-scan-target");
  }
  if (input.hasAttribute("data-sgs-ime")) {
    input.removeAttribute("data-sgs-ime");
  }
  if (input.hasAttribute("data-sgs-inputmode")) {
    input.removeAttribute("data-sgs-inputmode");
  }
  if (!input.hasAttribute(SGS_TYPING_FIELD_ATTRIBUTE)) {
    input.setAttribute(SGS_TYPING_FIELD_ATTRIBUTE, "");
  }
}

export function handleFloorSerialEnterKey(
  event: KeyboardEvent<HTMLInputElement>,
  submit: () => void,
): void {
  if (event.key !== "Enter") {
    return;
  }
  event.preventDefault();
  submit();
}
